import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import PageContainer from '../../../shared/layout/containers/PageContainer';
import PageHeader from '../../../shared/layout/headers/PageHeader';
import PageTitle from '../../../shared/layout/titles/PageTitle';
import PageContent from '../../../shared/layout/containers/PageContent';
import AdminFilterBar from './components/AdminFilterBar';
import AdminDataTable from './components/AdminDataTable';
import MainButton from '../../../shared/layout/buttons/MainButton';
import StandardModal from '../../../shared/layout/modals/StandardModal';
import { fetchAdminContracts, updateAdminContract } from '../../../services/Http/AdminApi';

const AdminContracts = () => {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null);
    const [draft, setDraft] = useState({});

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const resp = await fetchAdminContracts({ per_page: 25 });
            setRows(resp?.data || []);
        } catch (error) {
            toast.error(error?.response?.data?.message || 'Failed to load contracts');
        } finally {
            setLoading(false);
        }
    }, []);
    useEffect(() => { load(); }, [load]);

    const openEdit = (row) => {
        setEditing(row);
        setDraft({
            transaction_fee: row.transaction_fee ?? 0,
            fixed_transaction_fee: row.fixed_transaction_fee ?? 0,
            cross_sell_commission_percentage: row.cross_sell_commission_percentage ?? 0,
            active: !!row.active,
        });
    };

    const save = async () => {
        try {
            await updateAdminContract(editing.id, draft);
            toast.success('Contract updated');
            setEditing(null);
            load();
        } catch (error) {
            toast.error(error?.response?.data?.message || 'Failed to update contract');
        }
    };

    const columns = useMemo(() => ([
        { key: 'organization_name', label: 'Organization' },
        { key: 'start_date', label: 'Start' },
        { key: 'end_date', label: 'End' },
        { key: 'transaction_fee', label: 'Tx Fee %' },
        { key: 'fixed_transaction_fee', label: 'Fixed Fee' },
        { key: 'cross_sell_commission_percentage', label: 'Cross-sell %' },
        { key: 'active', label: 'Active', render: (r) => r.active ? 'Yes' : 'No' },
        { key: 'actions', label: 'Actions', render: (r) => <MainButton size="small" variant="secondary" onClick={() => openEdit(r)}>Edit</MainButton> },
    ]), []);

    return (
        <PageContainer>
            <PageHeader><PageTitle>Contracts</PageTitle></PageHeader>
            <PageContent>
                <AdminFilterBar><div>Global contract terms and fee configuration</div><MainButton size="small" onClick={load}>Refresh</MainButton></AdminFilterBar>
                <AdminDataTable columns={columns} rows={rows} loading={loading} emptyText="No contracts found" />
            </PageContent>

            <StandardModal
                isOpen={!!editing}
                onClose={() => setEditing(null)}
                title={`Edit Contract${editing?.organization_name ? ` · ${editing.organization_name}` : ''}`}
                footer={(
                    <>
                        <MainButton variant="secondary" onClick={() => setEditing(null)}>Cancel</MainButton>
                        <MainButton onClick={save}>Save</MainButton>
                    </>
                )}
            >
                <FormGrid>
                    <label>
                        Transaction Fee %
                        <input type="number" step="0.0001" value={draft.transaction_fee ?? ''} onChange={(e) => setDraft((d) => ({ ...d, transaction_fee: e.target.value }))} />
                    </label>
                    <label>
                        Fixed Transaction Fee
                        <input type="number" step="0.01" value={draft.fixed_transaction_fee ?? ''} onChange={(e) => setDraft((d) => ({ ...d, fixed_transaction_fee: e.target.value }))} />
                    </label>
                    <label>
                        Cross-sell Commission %
                        <input type="number" step="0.01" value={draft.cross_sell_commission_percentage ?? ''} onChange={(e) => setDraft((d) => ({ ...d, cross_sell_commission_percentage: e.target.value }))} />
                    </label>
                    <label className="checkbox">
                        <input type="checkbox" checked={!!draft.active} onChange={(e) => setDraft((d) => ({ ...d, active: e.target.checked }))} />
                        Active
                    </label>
                </FormGrid>
            </StandardModal>
        </PageContainer>
    );
};

export default AdminContracts;

const FormGrid = styled.div`
    display: grid;
    gap: 0.75rem;
    label {
        display: flex;
        flex-direction: column;
        gap: 0.375rem;
        color: ${({ theme }) => theme.colors.textPrimary || '#111827'};
        font-weight: 600;
        font-size: 0.875rem;
    }
    input {
        border: 1px solid ${({ theme }) => theme.colors.border || '#e5e7eb'};
        border-radius: 10px;
        padding: 0.625rem 0.75rem;
        font-weight: 500;
    }
    .checkbox {
        flex-direction: row;
        align-items: center;
    }
`;

